import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import { fileToBase64 } from "../helpers/utils";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import LoginState from "./LoginState";
import { useParams } from "react-router-dom";
import CollapsibleCard from "./CollapsibleCard";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import Stack from "@mui/material/Stack";
import FileInput from "./FileInput";

export default function OrderDeskReview({ order, afterCompleted }) {
  const [deskReviewFile, setDeskReviewFile] = useState();
  const [newFiles, setNewFiles] = useState([]);
  const [isSubmitting, setSubmitting] = useState(null);
  const [alert, setAlert] = useState();
  const [message, setMessage] = useState();
  const params = useParams();

  const _getUploadData = async () => {
    let response = await axios.get("/getdeskreviewfile", {
      params: { pOrderID: params.id },
    });
    if (response.data) {
      setDeskReviewFile(response.data);
    }
  };

  useEffect(() => {
    if (!deskReviewFile) {
      _getUploadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deskReviewFile);

  const MAX_FILE_SIZE = 25000000;

  const _combineFiles = (file1, file2) => {
    return file1 + "$$$$$" + file2;
  };

  const _uploadToServer = async () => {
    if (newFiles.length === 0) {
      return;
    }
    // check to see that combined file size is not over limit
    let totalSize = 0;
    for (let i = 0; i < newFiles.length; i++) {
      totalSize += newFiles[i].size;
    }
    if (totalSize >= MAX_FILE_SIZE) {
      setAlert("Combined File Size Exceeds 25Mb!");
      setMessage("Combined File Size Exceeds 25Mb!");
      return;
    }
    setSubmitting(true);

    // first combine all the files into one.
    let combinedFiles = null;
    for (let j = 0; j < newFiles.length; j++) {
      if (j === 0) combinedFiles = newFiles[j].fileUIntArr;
      else
        combinedFiles = _combineFiles(combinedFiles, newFiles[j].fileUIntArr);
    }
    // also combine the file names
    let combinedFileNames = null;
    for (let k = 0; k < newFiles.length; k++) {
      if (k === 0) combinedFileNames = newFiles[k].fileName;
      else
        combinedFileNames = _combineFiles(
          combinedFileNames,
          newFiles[k].fileName
        );
    }

    // then submit to server
    const params = {
      orderID: order.orderID,
      fileNames: combinedFileNames,
      fileContent: combinedFiles,
      userName: LoginState.userName,
    };

    axios.post("/FileUploadDeskReview", params)
      .then((response) => {
        const data = String(response.data);
        // error check
        if (!data.startsWith("Success")) {
          // show error message
          setSubmitting(false);
          setAlert("Error Uploading File(s)!");
          setMessage("Error Uploading File(s)!");
        } else {
          // notify for successful upload
          setSubmitting(false);
          setNewFiles([]);
          setAlert("File(s) Successfully Uploaded!");
          setMessage("File(s) Successfully Uploaded!");
          _getUploadData();
          afterCompleted();
        }
      })
      .catch(() => {
        setSubmitting(false);
        setAlert("Error Uploading File(s)!");
        setMessage("Error Uploading File(s)!");
      });
  };

  const _downloadFile = async (fileName) => {
    var response = axios.get("/getfiledata", {
      params: { pOrderID: order.orderID, pFileName: fileName },
    });
    var resultData = null;
    try {
      resultData = (await response).data;
    } catch {
      setAlert("Error Downloading File!");
      setMessage("Error Downloading File!");
    }

    var dlFN = fileName;
    if (fileName[fileName.length - 1] === ")") {
      dlFN = "";
      var splitFN = fileName.split(" ");
      for (var i = 0; i < splitFN.length - 1; i++) {
        dlFN = dlFN + splitFN[i];
      }
    }

    var binary = atob(resultData);
    var array = [];
    for (var j = 0; j < binary.length; j++) {
      array.push(binary.charCodeAt(j));
    }
    var file = new Blob([new Uint8Array(array)]);

    // this code below will download the file we selected
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.download = dlFN;
    a.click();
    URL.revokeObjectURL(url);
  };

  const _handleChangeStatus = async (acceptedFiles) => {
    setSubmitting(true);
    for(var i=0; i<acceptedFiles.length; i++) {
      const uintarr = await fileToBase64(acceptedFiles[i]);
      setNewFiles([
        {
          fileUIntArr: uintarr,
          fileName: acceptedFiles[i].name,
          size: acceptedFiles[i].size,
        },
      ]);
    }
    setSubmitting(false);
  }

  const _deleteFile = (fileName) => {
    let newArr = [...newFiles];
    for (let i = 0; i < newArr.length; i++) {
      if (newArr[i].fileName === fileName) {
        newArr.splice(i, 1);
        setNewFiles(newArr);
        break;
      }
    }
  };

  return (
    <CollapsibleCard title="Desk Review File">
      {alert && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {alert}
        </Alert>
      )}

      {LoginState.userRole === "Admin" && (
        <>
          <Typography variant="subtitle1" mb={2}>
            Upload File For Appraiser Review
          </Typography>
          <FileInput
            onFileDrop={_handleChangeStatus}
            disabled={isSubmitting}
            formats="PDF only"
          />
          {message && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}
          {newFiles.length > 0 &&
            newFiles.map((file) => (
              <Box
                sx={{
                  backgroundColor: "grey.100",
                  p: 1,
                  pl: 2,
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Typography variant="body1">
                  {file.fileName}{" "}
                  <Chip
                    size="small"
                    variant="outlined"
                    label="New"
                    color="success"
                  />
                </Typography>
                <IconButton sx={{ m: 0 }}>
                  <DeleteIcon onClick={() => _deleteFile(file.fileName)} />
                </IconButton>
              </Box>
            ))}
        </>
      )}

      {deskReviewFile && (
        <Box
          sx={{
            backgroundColor: "grey.100",
            p: 1,
            pl: 2,
            borderRadius: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "15px 0px",
          }}
        >
          <Typography variant="body1">{deskReviewFile}</Typography>
          <IconButton sx={{ m: 0 }}>
            <DownloadIcon onClick={() => _downloadFile(deskReviewFile)} />
          </IconButton>
        </Box>
      )}

      {LoginState.userRole === "Admin" && (
        <>
          <Box>
            {isSubmitting ? (
              <Stack justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
              </Stack>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "10px",
                }}
              >
                <Box>
                  <Button
                    disableElevation
                    disabled={isSubmitting || newFiles.length === 0}
                    variant="contained"
                    onClick={_uploadToServer}
                  >
                    {deskReviewFile ? "Replace File" : "Submit File"}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}
    </CollapsibleCard>
  );
}
